import { LitElement, css, html, nothing } from "lit";

import { customElement, state } from "lit/decorators.js";

import { WowFormController } from "ui-common";
import { appContext } from "ui-common/appContext.ts";
import { rpc } from "ui-common/services/rpc";
import "ui-components/button/wow-button.ts";
import "ui-components/divider/divider";
import "ui-components/form-elements/input/wow-input.ts";
import "ui-components/social-login-buttons/apple-login-button";
import "ui-components/social-login-buttons/facebook-login-button";
import "ui-components/social-login-buttons/google-login-button";
import "ui-components/wow-link/wow-link";
import "ui-components/wow-logo/wow-logo";

import "@phosphor-icons/webcomponents/PhCircleNotch";

import privatePolicy from "../../assets/Privacy-Policy.pdf";
import termsAndConditions from "../../assets/Terms-and-Conditions.pdf";
import { navigateTo } from "../router";
import { t, translate } from "../translations/i18n";
import { isFan } from "helpers/minimalViableProfile";
import { isValidRedirectUrl } from "helpers/url";
import { processPendingCheckout } from "ui-components/process-stripe-checkout";

const styles = css`
  :host {
    display: block;
    min-height: 100dvh;
    background-color: var(--sds-color-background-default-default);
    color: var(--sds-color-text-default-default);
    font-family: var(--sds-typography-family-body);
  }

  /* Base layout utilities */
  .container,
  .main-content,
  .header,
  .title-section,
  .form-section,
  .footer {
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
  }

  .container {
    width: 100%;
    max-width: 393px;
    margin: 0 auto;
    padding: var(--sds-size-space-800);
    min-height: 100dvh;
  }

  .logo {
    margin-bottom: var(--sds-size-space-1600);
  }

  .main-content {
    gap: var(--sds-size-space-800);
  }

  .header,
  .title-section {
    gap: var(--sds-size-space-800);
  }

  .title-section {
    gap: var(--sds-size-space-400);
  }

  .title {
    font: var(--sds-typography-weight-medium) var(--sds-typography-scale-06) / 1.2 var(--sds-typography-family-title);
  }

  .subtitle,
  .terms-text,
  .login-link {
    font-family: var(--sds-typography-family-body);
    font-weight: var(--sds-typography-weight-regular);
  }

  .subtitle {
    font-size: var(--sds-typography-scale-03);
    line-height: 1.4;
    color: var(--sds-color-text-default-secondary);
  }

  .terms-text {
    font-size: var(--sds-typography-scale-01);
    line-height: 1.5;
    color: var(--sds-color-text-default-tertiary);
    max-width: 345px;
  }

  .login-link {
    font-size: var(--sds-typography-scale-02);
    line-height: 1.4;
    text-align: center;
    text-decoration: none;
  }

  .login-link:hover {
    text-decoration: underline;
  }

  .social-buttons {
    display: flex;
    gap: 11.57px;
    align-items: center;
  }

  .form-section {
    gap: var(--sds-size-space-600);
  }

  .input-field {
    background-color: var(--sds-color-background-default-secondary);
    border: var(--sds-size-stroke-border) solid var(--sds-color-border-default-default);
    border-radius: var(--sds-size-radius-400);
    padding: var(--sds-size-space-400) var(--sds-size-space-600);
    font: var(--sds-typography-weight-medium) var(--sds-typography-scale-03) / 1 var(--sds-typography-family-title);
    color: var(--sds-color-text-default-default);
    width: 100%;
    box-sizing: border-box;
  }

  .input-field::placeholder {
    color: var(--sds-color-text-default-tertiary);
  }

  .input-field:focus {
    outline: none;
    border-color: var(--sds-color-border-default-secondary);
    background-color: var(--sds-color-background-default-default-hover);
  }

  .submit-button {
    display: grid;
  }

  .footer {
    gap: var(--sds-size-space-1200);
    margin-top: var(--sds-size-space-800);
  }

  .form-error {
    color: var(--sds-color-text-danger-default);
    font-size: var(--sds-typography-scale-02);
    margin-top: var(--sds-size-space-300);
    text-align: center;
  }

  form {
    display: contents;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

interface SignInFormData {
  email: string;
  password: string;
}

@customElement("sign-in-page")
export class SignInPage extends LitElement {
  static override styles = [styles];

  @state()
  private formError = "";

  @state()
  private isProcessingCheckout = false;

  @state()
  private checkoutError = "";

  @state()
  private isFormInitialized = false;

  @state()
  private isPurchaseMode = false;

  override connectedCallback() {
    super.connectedCallback();
    this.checkForOAuthError();
    this.handleOAuthReturn();
  }

  override firstUpdated() {
    // Handle guest checkout params after form is rendered
    this.handleGuestCheckoutParams();
  }

  private handleGuestCheckoutParams() {
    if (this.isFormInitialized) return;

    const urlParams = new URLSearchParams(window.location.search);
    const prefilledEmail = urlParams.get("email");
    const returnUrl = urlParams.get("return");
    const autoCheckout = urlParams.get("autoCheckout");

    this.isPurchaseMode = !!autoCheckout;

    // Only store returnUrl if it passes security validation
    if (returnUrl && isValidRedirectUrl(returnUrl)) {
      sessionStorage.setItem("postLoginReturn", returnUrl);
    }

    if (prefilledEmail) {
      // Use form controller's reset method to initialize with prefilled email
      // This happens after firstUpdated, so the form element exists
      this.formController.reset({
        email: prefilledEmail,
        password: "",
      });
      this.isFormInitialized = true;
    }
  }

  private checkForOAuthError() {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get("error");

    if (error) {
      this.formError = "Sign-in failed, try later"; // `Authentication failed: ${decodeURIComponent(error)}`;

      // Clean up URL to remove error parameter
      const cleanUrl = window.location.pathname;
      window.history.replaceState(null, "", cleanUrl);
    }
  }

  private async handleOAuthReturn() {
    const urlParams = new URLSearchParams(window.location.search);
    const oauthComplete = urlParams.get("oauthComplete");

    if (oauthComplete !== "true") return;

    // OAuth just completed - session cookie should have been set by the OAuth callback
    // Clean the oauthComplete param from URL while preserving checkout params
    urlParams.delete("oauthComplete");
    const newSearch = urlParams.toString();
    const cleanUrl = newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname;
    window.history.replaceState(null, "", cleanUrl);

    // Set purchase mode for UI
    const autoCheckout = urlParams.get("autoCheckout");
    this.isPurchaseMode = !!autoCheckout;

    // Wait for session initialization to complete (appContext attempts session auth on startup)
    await appContext.ready;

    if (appContext.isAuthenticated) {
      // Process auto-checkout with the URL params
      await this.processAutoCheckout(urlParams);
    }
  }

  private async processAutoCheckout(urlParams: URLSearchParams) {
    const autoCheckout = urlParams.get("autoCheckout");
    const eventId = urlParams.get("eventId");
    const contentId = urlParams.get("contentId");
    const rawReturnUrl = urlParams.get("return");

    // Validate returnUrl to prevent open redirect attacks
    const returnUrl = rawReturnUrl && isValidRedirectUrl(rawReturnUrl) ? rawReturnUrl : null;

    // Handle post/link auto-checkout
    if (returnUrl && (autoCheckout === "post" || autoCheckout === "link") && contentId) {
      try {
        const checkoutResponse = await rpc("create-one-time-checkout", {
          contentType: autoCheckout as "post" | "link",
          contentId: contentId,
          returnUrl: returnUrl,
        });
        window.location.href = checkoutResponse.url;
        return;
      } catch (error) {
        console.error("Auto-checkout error:", error);
        window.location.href = returnUrl;
        return;
      }
    }

    // Handle live-event auto-checkout
    if (returnUrl && autoCheckout === "live-event" && eventId) {
      // Extract handle from return URL to check access
      const oldPatternMatch = returnUrl.match(/\/([^/]+)\/live-events/);
      const newPatternMatch = returnUrl.match(/\/live-event\/([^/]+)\//);
      const handle = oldPatternMatch?.[1] || newPatternMatch?.[1] || null;

      if (handle) {
        try {
          // Check if user already has access to this event
          const eventsResponse = await rpc("get-public-live-events", { handle });

          if (eventsResponse.events) {
            const event = eventsResponse.events.find((e) => e.id === eventId);

            if (event?.hasAccess) {
              // User already purchased - redirect to event page with purchased flag
              const separator = returnUrl.includes("?") ? "&" : "?";
              window.location.href = `${returnUrl}${separator}purchased=true`;
              return;
            }
          }

          // User needs to purchase - proceed to checkout
          const checkoutResponse = await rpc("create-one-time-checkout", {
            contentType: "live-event",
            contentId: eventId,
            returnUrl: returnUrl,
          });

          window.location.href = checkoutResponse.url;
          return;
        } catch (error) {
          console.error("Auto-checkout error:", error);
          window.location.href = returnUrl;
          return;
        }
      }

      // Fallback if handle extraction failed
      window.location.href = returnUrl;
      return;
    }

    // Check for guest checkout return URL (non-auto-checkout case)
    if (returnUrl) {
      window.location.href = returnUrl;
      return;
    }

    // Default navigation to profile
    navigateTo("profile");
  }

  private formController: WowFormController<SignInFormData> = new WowFormController(
    this,
    {
      email: "",
      password: "",
    },
    {
      alwaysValidateOnInput: false,
      validateOnBlur: true,
      validateOnInputWhenDirty: false,
      validators: {
        email: [
          (value: string) => {
            if (!value) {
              return translate("sign-in.emailRequired");
            }
            if (!/\S+@\S+\.\S+/.test(value)) {
              return translate("sign-in.invalidEmail");
            }
            return;
          },
        ],
        password: [
          (value: string) => {
            if (!value) return translate("sign-in.passwordRequired");
            if (value.length < 6) return translate("sign-in.passwordMinLength");
            return;
          },
        ],
      },
    },
    async (event) => {
      this.formError = "";

      if (!event || !event.data || !event.isValid) {
        return;
      }

      const { data, isValid } = event;
      if (!isValid) {
        this.formError = translate("sign-in.fixErrors");
        return;
      }

      try {
        const result = await appContext.SignIn(event.data.email.toLowerCase(), data.password);

        if (!result) {
          this.formError = translate("sign-in.invalidCredentials");
        } else {
          // Check for auto-checkout (live-event, post, or link)
          const urlParams = new URLSearchParams(window.location.search);
          const autoCheckout = urlParams.get("autoCheckout");
          const eventId = urlParams.get("eventId");
          const contentId = urlParams.get("contentId");
          // Try sessionStorage first, fall back to URL param (URL param is already decoded by URLSearchParams)
          const storedReturnUrl = sessionStorage.getItem("postLoginReturn");
          const urlReturnParam = urlParams.get("return");
          const rawReturnUrl = storedReturnUrl || urlReturnParam;
          // Validate returnUrl to prevent open redirect attacks
          const returnUrl = rawReturnUrl && isValidRedirectUrl(rawReturnUrl) ? rawReturnUrl : null;

          // Handle post/link auto-checkout
          if (returnUrl && (autoCheckout === "post" || autoCheckout === "link") && contentId) {
            sessionStorage.removeItem("postLoginReturn");
            try {
              const checkoutResponse = await rpc("create-one-time-checkout", {
                contentType: autoCheckout as "post" | "link",
                contentId: contentId,
                returnUrl: returnUrl,
              });
              window.location.href = checkoutResponse.url;
              return;
            } catch (error) {
              console.error("Auto-checkout error:", error);
              window.location.href = returnUrl;
              return;
            }
          }

          // Handle live-event auto-checkout
          if (returnUrl && autoCheckout === "live-event" && eventId) {
            sessionStorage.removeItem("postLoginReturn");

            // Extract handle from return URL to check access
            // Supports both URL patterns:
            // - Old: /{handle}/live-events/{eventId}
            // - New: /live-event/{handle}/{eventId}
            const oldPatternMatch = returnUrl.match(/\/([^/]+)\/live-events/);
            const newPatternMatch = returnUrl.match(/\/live-event\/([^/]+)\//);
            const handle = oldPatternMatch?.[1] || newPatternMatch?.[1] || null;

            if (handle) {
              try {
                // Check if user already has access to this event
                const eventsResponse = await rpc("get-public-live-events", { handle });

                if (eventsResponse.events) {
                  const event = eventsResponse.events.find((e) => e.id === eventId);

                  if (event?.hasAccess) {
                    // User already purchased - redirect to event page with purchased flag
                    const separator = returnUrl.includes("?") ? "&" : "?";
                    window.location.href = `${returnUrl}${separator}purchased=true`;
                    return;
                  }
                }

                // User needs to purchase - proceed to checkout
                const checkoutResponse = await rpc("create-one-time-checkout", {
                  contentType: "live-event",
                  contentId: eventId,
                  returnUrl: returnUrl,
                });

                window.location.href = checkoutResponse.url;
                return;
              } catch (error) {
                console.error("Auto-checkout error:", error);
                // Fallback to normal return URL redirect on error
                window.location.href = returnUrl;
                return;
              }
            }

            // Fallback if handle extraction failed
            window.location.href = returnUrl;
            return;
          }

          // Check for guest checkout return URL (non-auto-checkout case)
          if (returnUrl) {
            sessionStorage.removeItem("postLoginReturn");
            window.location.href = returnUrl;
            return;
          }

          // Check for pending checkout
          const checkoutData = sessionStorage.getItem("pendingCheckout");

          if (checkoutData) {
            // Process pending checkout for returning users
            const out = await processPendingCheckout();
            this.isProcessingCheckout = out!.isProcessingCheckout;
            this.checkoutError = out!.checkoutError;
          } else {
            // Conditional navigation based on user type
            if (result.id && isFan(result)) {
              // Fan user: has name and id, but no handle/avatar - redirect to explore page
              window.location.href = "/explore";
            } else {
              // Creator user: has handle and avatar - stay in backoffice/profile page
              navigateTo("profile");
            }
          }
        }
      } catch (error) {
        this.formError = translate("sign-in.generalError");
        console.error("Sign in error:", error);
      }
    },
  );

  protected override render() {
    if (this.isProcessingCheckout) {
      return html`
        <div class="container">
          <div class="logo">
            <wow-logo></wow-logo>
          </div>
          <div class="main-content" style="text-align: center; justify-content: center;">
            <h2 class="title">Processing your purchase...</h2>
            <p class="subtitle">Please wait while we redirect you to checkout.</p>
            <ph-circle-notch
              size="48px"
              style="animation: spin 1s linear infinite; margin: 24px auto;"
            ></ph-circle-notch>
          </div>
        </div>
      `;
    }

    if (this.checkoutError) {
      return html`
        <div class="container">
          <div class="logo">
            <wow-logo></wow-logo>
          </div>
          <div class="main-content">
            <h2 class="title">Checkout Error</h2>
            <p style="color: var(--color-error, #dc2626); margin: 16px 0;">${this.checkoutError}</p>
            <wow-button
              @click=${() => {
                this.checkoutError = "";
                void processPendingCheckout();
              }}
            >
              Try Again
            </wow-button>
            <wow-button href="/explore" variant="neutral" style="margin-top: 12px;"> Continue to WOW </wow-button>
          </div>
        </div>
      `;
    }

    return html`
      <div class="container">
        <div class="logo">
          <wow-logo></wow-logo>
        </div>

        <div class="main-content">
          <div class="header">
            <div class="title-section">
              <h1 class="title">${this.isPurchaseMode ? t("sign-in.purchaseTitle") : t("sign-in.title")}</h1>
              ${this.isPurchaseMode ? nothing : html`<p class="subtitle">${t("sign-in.subtitle")}</p>`}
            </div>

            <div class="social-buttons">
              <google-login-button></google-login-button>
              <!-- <apple-login-button></apple-login-button> -->
              <facebook-login-button></facebook-login-button>
            </div>
            <wow-divider>${t("sign-in.or")}</wow-divider>
          </div>

          <form id="sign-in-form">
            <div class="form-section">
              <wow-input
                name=${this.formController.fieldNames.email}
                type="email"
                placeholder=${t("sign-in.email")}
                autocomplete="email"
                required
              >
              </wow-input>

              <wow-input
                name=${this.formController.fieldNames.password}
                type="password"
                placeholder=${t("sign-in.password")}
                autocomplete="current-password"
                required
              ></wow-input>

              <p style="text-align: right; margin: 0;">
                <wow-link href="forgot-password">${t("sign-in.forgotPassword")}</wow-link>
              </p>

              ${this.formError ? html`<div class="form-error">${this.formError}</div>` : ""}

              <wow-button form="sign-in-form" class="submit-button" type="submit">${t("sign-in.signIn")}</wow-button>
            </div>
          </form>
        </div>

        <div class="footer">
          ${this.isPurchaseMode
            ? nothing
            : html`<p>
                ${t("sign-in.noAccount")}
                <wow-link href="sign-up"> ${t("sign-in.signUp")} </wow-link>
              </p>`}
          <p class="terms-text">
            ${t("sign-in.termsAndConditions")}
            <a href=${"/static/" + termsAndConditions} target="_blank" rel="noopener noreferrer" style="color: inherit;"
              >${t("sign-in.termsOfServiceLink")}</a
            >
            ${t("sign-in.and")}
            <a href=${"/static/" + privatePolicy} target="_blank" rel="noopener noreferrer" style="color: inherit;"
              >${t("sign-in.privacyPolicyLink")}</a
            >
            ${t("sign-in.byWorldOfWow")}
          </p>
        </div>
      </div>
    `;
  }
}
